/**
 * @file /discover route definitions.
 */

import React from 'react';
import { compose } from 'redux';
import { Redirect } from 'react-router';

import RequireValidAlbumFolder from '../components/routing/RequireValidAlbumFolder';
import RequireAuth from '../components/routing/discover/RequireAuth';
import DiscoverAlbumFolderRedirectContainer from '../containers/discover/DiscoverAlbumFolderRedirectContainer';
import DiscoverAlbumFolderMasterView from '../views/discover/DiscoverAlbumFolderMasterView';
import DiscoverAlbumFolderStartView from '../views/discover/DiscoverAlbumFolderStartView';
import DiscoverAlbumFolderView from '../views/discover/DiscoverAlbumFolderView';
import DiscoverAlbumFolderFacesView from '../views/discover/DiscoverAlbumFolderFacesView';
import DiscoverAlbumFolderSummaryView from '../views/discover/DiscoverAlbumFolderSummaryView';
import DiscoverAlbumFolderSuccessView from '../views/discover/DiscoverAlbumFolderSuccessView';
import SetAlbumFolder from '../components/routing/discover/SetAlbumFolder';
import RequireIdentity from '../components/routing/discover/RequireIdentity';
import RequireSelectedAlbums from '../components/routing/discover/RequireSelectedAlbums';
import RequirePurchasedAlbumFolder from '../components/routing/discover/RequirePurchasedAlbumFolder';
import AlbumFolderAllowsPurchaseOnly from '../components/routing/discover/AlbumFolderAllowsPurchaseOnly';
import SetAlbumFolderJoinPreferences from '../components/routing/discover/SetAlbumFolderJoinPreferences';
import RedirectToJoinTypePreselection from '../components/routing/discover/RedirectToJoinTypePreselection';
import OnboardingAccessRequestView from '../views/onboarding/access-control/OnboardingAccesslRequestView';
import AlbumFolderAccessControl from '../components/routing/discover/AlbumFolderAccessControl';
import RequireIdentitiesTags from '../components/routing/discover/RequireIdentitiesTags';
import withGTMCustomDimensions from '../components/routing/discover/withGTMCustomDimensions';
import { PhotoSearchUpsellCheckout } from '../apps/temp/views/onboarding/PhotoSearchUpsell';
import withOutdatedAlbumFolderModal from '../components/routing/withOutdatedAlbumFolderModal';
import withHierarchyViewsCreate from '../components/global/HiearchyGroups/hoc/withHierarchyViewsCreate';
import DiscoverAlbumFolderPrepaidItems from '../components/discover/DiscoverAlbumFolderPrepaidItems';
import AlbumFolderAllowsPrePaidItems from '../components/routing/discover/AlbumFolderAllowsPrePaidItems';
import SetPlicCheckoutFlag from '../components/routing/discover/SetPlicCheckoutFlag';

const routes = [
  {
    path: '/discover/album-folder/redirect',
    exact: false,
    component: DiscoverAlbumFolderRedirectContainer,
  },
  {
    path: '/discover/album-folder/:albumFolderId/request-access',
    exact: true,
    component: compose(RequireAuth)(OnboardingAccessRequestView),
  },
  {
    path: '/discover/album-folder/:albumFolderId',
    exact: false,
    component: compose(
      withGTMCustomDimensions,
      RequireValidAlbumFolder,
      AlbumFolderAccessControl,
      SetAlbumFolder,
      SetPlicCheckoutFlag,
      RedirectToJoinTypePreselection,
      SetAlbumFolderJoinPreferences,
      withOutdatedAlbumFolderModal,
      withHierarchyViewsCreate(({ match }) => match.params.albumFolderId),
    )(DiscoverAlbumFolderMasterView),
    routes: [
      {
        path: '/discover/album-folder/:albumFolderId/start',
        exact: true,
        component: compose(
          AlbumFolderAllowsPrePaidItems({ forceRedirect: true }),
        )(DiscoverAlbumFolderStartView),
      },
      {
        path: '/discover/album-folder/:albumFolderId',
        exact: true,
        component: compose(
          RequireAuth,
          RequireIdentity,
          AlbumFolderAllowsPrePaidItems({ forceRedirect: true }),
        )(({ match }) => (
          <Redirect
            to={`/discover/album-folder/${match.params.albumFolderId}/faces`}
          />
        )),
      },
      {
        path: '/discover/album-folder/:albumFolderId/select',
        exact: true,
        component: compose(
          RequireAuth,
          RequireIdentity,
        )(DiscoverAlbumFolderView),
      },
      {
        path: '/discover/album-folder/:albumFolderId/faces',
        exact: true,
        component: compose(
          RequireAuth,
          AlbumFolderAllowsPurchaseOnly,
          RequirePurchasedAlbumFolder,
        )(DiscoverAlbumFolderFacesView),
      },
      {
        path: '/discover/album-folder/:albumFolderId/summary',
        exact: true,
        component: compose(
          RequireAuth,
          RequireSelectedAlbums,
          RequireIdentitiesTags,
          AlbumFolderAllowsPrePaidItems(),
        )(DiscoverAlbumFolderSummaryView),
      },
      {
        path: '/discover/album-folder/:albumFolderId/pre-purchase',
        exact: true,
        component: compose(
          RequireAuth,
          AlbumFolderAllowsPrePaidItems(),
        )(DiscoverAlbumFolderPrepaidItems),
      },
      {
        path: '/discover/album-folder/:albumFolderId/photo-search-checkout',
        exact: true,
        // Imported from the new consumer 2.0 app as it is developed as a new
        // component of that flow.
        component: compose(RequireAuth)(PhotoSearchUpsellCheckout),
      },
      {
        path: '/discover/album-folder/:albumFolderId/success',
        exact: true,
        component: compose(RequireAuth)(DiscoverAlbumFolderSuccessView),
      },
    ],
  },
];

export default routes;
